import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { format } from 'date-fns'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import Textarea from '../components/ui/Textarea'
import Modal from '../components/ui/Modal'
import RichContentEditor from '../components/RichContentEditor'
import HeroImageUploader from '../components/HeroImageUploader'
import { ToastProvider, useToast } from '../components/ui/Toast'

const { FiArrowLeft, FiSave, FiTrash2, FiClock, FiEye } = FiIcons

// Utility function to normalize content before setting in state
const normalizePostContent = (content) => {
  // If content is empty or null, return stringified empty TipTap doc
  if (!content) {
    return JSON.stringify({ type: 'doc', content: [{ type: 'paragraph' }] })
  }
  
  // If content is already a string
  if (typeof content === 'string') {
    try {
      // Try to parse it to validate it's valid JSON
      const parsed = JSON.parse(content)
      // If it parses successfully and looks like TipTap, return the original string
      if (parsed && typeof parsed === 'object' && parsed.type === 'doc') {
        return content
      }
      // If it's valid JSON but not TipTap format, return empty doc
      return JSON.stringify({ type: 'doc', content: [{ type: 'paragraph' }] })
    } catch (error) {
      // If it fails to parse, return empty doc
      console.warn('Invalid JSON content, using empty document:', error)
      return JSON.stringify({ type: 'doc', content: [{ type: 'paragraph' }] })
    }
  }
  
  // If content is an object, stringify it
  if (typeof content === 'object') {
    try {
      return JSON.stringify(content)
    } catch (error) {
      console.warn('Failed to stringify content object, using empty document:', error)
      return JSON.stringify({ type: 'doc', content: [{ type: 'paragraph' }] })
    }
  }
  
  // For any other type, return empty doc
  return JSON.stringify({ type: 'doc', content: [{ type: 'paragraph' }] })
}

const PostEditorContent = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  
  const [post, setPost] = useState(null)
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [slugError, setSlugError] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const saveTimeoutRef = useRef(null)
  const originalPostRef = useRef(null)

  useEffect(() => {
    if (id) {
      fetchPost()
      fetchCategories()
    }
  }, [id])

  // Auto-save effect
  useEffect(() => {
    if (hasUnsavedChanges && post) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      
      saveTimeoutRef.current = setTimeout(() => {
        handleSave(true) // silent save
      }, 800)
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [hasUnsavedChanges, post])

  // Page leave guard
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const fetchPost = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      if (!data) {
        toast.error('Post not found')
        navigate('/posts')
        return
      }

      // Normalize the content before setting the post state
      const normalizedData = {
        ...data,
        content: normalizePostContent(data.content)
      }

      setPost(normalizedData)
      originalPostRef.current = { ...normalizedData }
    } catch (error) {
      console.error('Error fetching post:', error)
      toast.error('Failed to load post')
      navigate('/posts')
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_categories')
        .select('id, name')
        .order('name')

      if (error) throw error
      setCategories(data || [])
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const updatePost = useCallback((updates) => {
    setPost(prev => {
      const updated = { ...prev, ...updates }
      
      // Check if there are actual changes
      const hasChanges = JSON.stringify(updated) !== JSON.stringify(originalPostRef.current)
      setHasUnsavedChanges(hasChanges)
      
      return updated
    })
  }, [])

  const generateSlugFromTitle = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }

  const handleTitleChange = (e) => {
    const newTitle = e.target.value
    updatePost({ title: newTitle })
    
    // Auto-generate slug if this is the first edit or slug is still default
    if (!post.slug || post.slug.startsWith('untitled')) {
      const newSlug = generateSlugFromTitle(newTitle)
      updatePost({ title: newTitle, slug: newSlug })
    } else {
      updatePost({ title: newTitle })
    }
  }

  const validateSlugUniqueness = async (slug) => {
    if (!slug || slug === originalPostRef.current?.slug) {
      setSlugError('')
      return true
    }

    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id')
        .ilike('slug', slug)
        .neq('id', id)
        .limit(1)

      if (error) throw error

      if (data && data.length > 0) {
        setSlugError('This slug is already in use')
        return false
      }

      setSlugError('')
      return true
    } catch (error) {
      console.error('Error validating slug:', error)
      setSlugError('Error validating slug')
      return false
    }
  }

  const handleSlugBlur = async (e) => {
    await validateSlugUniqueness(e.target.value)
  }

  const handleStatusChange = (e) => {
    const newStatus = e.target.value
    const updates = { status: newStatus }

    // If setting to published and no published_at date, prompt for it
    if (newStatus === 'published' && !post.published_at) {
      const shouldSetNow = window.confirm(
        'This post has no publish date. Set it to now?'
      )
      if (shouldSetNow) {
        updates.published_at = new Date().toISOString()
      }
    }

    updatePost(updates)
  }

  const handleSave = async (silent = false) => {
    if (slugError) {
      toast.error('Please fix the slug error before saving')
      return
    }

    try {
      setSaving(true)

      const { error } = await supabase
        .from('blog_posts')
        .update({
          ...post,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)

      if (error) throw error

      originalPostRef.current = { ...post }
      setHasUnsavedChanges(false)
      setLastSaved(new Date())
      
      if (!silent) {
        toast.success('Post saved successfully')
      }
    } catch (error) {
      console.error('Error saving post:', error)
      toast.error('Failed to save post')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      setDeleting(true)

      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id)

      if (error) throw error

      toast.success('Post deleted successfully')
      navigate('/posts')
    } catch (error) {
      console.error('Error deleting post:', error)
      toast.error('Failed to delete post')
    } finally {
      setDeleting(false)
      setShowDeleteModal(false)
    }
  }

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'published', label: 'Published' }
  ]

  const categoryOptions = [
    { value: '', label: 'No category' },
    ...categories.map(cat => ({ value: cat.id, label: cat.name }))
  ]

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => navigate('/posts')}>
                  <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
                  Back to Posts
                </Button>
                <h1 className="text-xl font-semibold text-gray-900">Loading...</h1>
              </div>
            </div>
          </div>
        </header>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    )
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Post not found</h1>
          <Button onClick={() => navigate('/posts')}>
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
            Back to Posts
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/posts')}>
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
                Back to Posts
              </Button>
              <h1 className="text-xl font-semibold text-gray-900 truncate max-w-md">
                {post.title}
              </h1>
              {hasUnsavedChanges && (
                <span className="text-sm text-amber-600 flex items-center gap-1">
                  <SafeIcon icon={FiClock} className="w-3 h-3" />
                  Unsaved changes
                </span>
              )}
              {lastSaved && !hasUnsavedChanges && (
                <span className="text-sm text-green-600">
                  Saved • {format(lastSaved, 'HH:mm:ss')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                onClick={() => handleSave(false)}
                loading={saving}
                disabled={!hasUnsavedChanges}
              >
                <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="space-y-4">
                <Input
                  label="Title"
                  value={post.title || ''}
                  onChange={handleTitleChange}
                  placeholder="Enter post title..."
                  className="text-lg font-semibold"
                />
                
                <Input
                  label="Subtitle"
                  value={post.subtitle || ''}
                  onChange={(e) => updatePost({ subtitle: e.target.value })}
                  placeholder="Enter post subtitle..."
                />
                
                <Input
                  label="Slug"
                  value={post.slug || ''}
                  onChange={(e) => updatePost({ slug: e.target.value })}
                  onBlur={handleSlugBlur}
                  placeholder="post-slug"
                  error={slugError}
                  className="font-mono text-sm"
                />
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Content</h3>
              <RichContentEditor
                content={post.content || ''}
                onChange={(content) => updatePost({ content })}
                placeholder="Start writing your post..."
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publish Settings */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Publish</h3>
              <div className="space-y-4">
                <Select
                  label="Status"
                  options={statusOptions}
                  value={post.status || 'draft'}
                  onChange={handleStatusChange}
                />
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Published At
                  </label>
                  <input
                    type="datetime-local"
                    value={post.published_at ? format(new Date(post.published_at), "yyyy-MM-dd'T'HH:mm") : ''}
                    onChange={(e) => updatePost({ 
                      published_at: e.target.value ? new Date(e.target.value).toISOString() : null 
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <Select
                  label="Category"
                  options={categoryOptions}
                  value={post.category_id || ''}
                  onChange={(e) => updatePost({ category_id: e.target.value || null })}
                />
              </div>
            </div>

            {/* Hero Image */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <HeroImageUploader
                postId={post.id}
                currentImageUrl={post.hero_image_url}
                onImageUpdate={(url) => updatePost({ hero_image_url: url })}
              />
              
              {post.hero_image_url && (
                <div className="mt-4">
                  <Input
                    label="Hero Image Alt Text"
                    value={post.hero_image_alt || ''}
                    onChange={(e) => updatePost({ hero_image_alt: e.target.value })}
                    placeholder="Describe the image..."
                  />
                </div>
              )}
            </div>

            {/* Excerpt */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <Textarea
                label="Excerpt"
                value={post.excerpt || ''}
                onChange={(e) => updatePost({ excerpt: e.target.value })}
                placeholder="Brief description of the post..."
                rows={3}
              />
            </div>

            {/* Author */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Author</h3>
              <div className="space-y-4">
                <Input
                  label="Author Name"
                  value={post.author_name || ''}
                  onChange={(e) => updatePost({ author_name: e.target.value })}
                  placeholder="Author name"
                />
                
                <Input
                  label="Author Avatar URL"
                  value={post.author_avatar_url || ''}
                  onChange={(e) => updatePost({ author_avatar_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>

            {/* SEO */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO</h3>
              <div className="space-y-4">
                <Input
                  label="Meta Title"
                  value={post.seo_meta_title || ''}
                  onChange={(e) => updatePost({ seo_meta_title: e.target.value })}
                  placeholder="SEO title for search engines"
                />
                
                <Textarea
                  label="Meta Description"
                  value={post.seo_meta_description || ''}
                  onChange={(e) => updatePost({ seo_meta_description: e.target.value })}
                  placeholder="SEO description for search engines"
                  rows={3}
                />
                
                <Input
                  label="Canonical URL"
                  value={post.canonical_url || ''}
                  onChange={(e) => updatePost({ canonical_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-white rounded-lg border border-red-200 p-6">
              <h3 className="text-lg font-semibold text-red-900 mb-4">Danger Zone</h3>
              <p className="text-sm text-gray-600 mb-4">
                Once you delete a post, there is no going back. Please be certain.
              </p>
              <Button
                variant="danger"
                onClick={() => setShowDeleteModal(true)}
                className="w-full"
              >
                <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-2" />
                Delete Post
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Post"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete "{post.title}"? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleting}
            >
              Delete Post
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

const PostEditor = () => {
  return (
    <ToastProvider>
      <PostEditorContent />
    </ToastProvider>
  )
}

export default PostEditor